import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, CheckCircle2, FileText, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

export function WeeklyReportCard() {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/weekly-report/history?limit=6');
      setHistory(data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm("Générer et envoyer le bilan hebdomadaire maintenant aux super_admin + admin ?")) return;
    setRunning(true);
    try {
      const { data } = await api.post('/weekly-report/generate-now');
      toast.success(data.email_sent ? "Bilan envoyé par email" : "Bilan généré (email non envoyé)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setRunning(false);
  };

  const downloadPreview = async () => {
    try {
      const { data } = await api.get('/weekly-report/preview');
      const byteChars = atob(data.pdf_base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan_wasabi_${data.week_start}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Card className="border-2 border-[#0EA5E9]/20 shadow-sm bg-gradient-to-br from-[#F0F9FF] to-white" data-testid="weekly-report-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#0EA5E9]/15 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-[#0EA5E9]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Bilan hebdomadaire</h3>
              <p className="text-xs text-[#71717A] mt-0.5">
                Envoi automatique chaque <strong>vendredi 18h00</strong> aux super_admin + admin, avec PDF en pièce jointe.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={downloadPreview}
              className="h-9 text-xs" data-testid="preview-weekly-btn">
              <FileText className="w-3.5 h-3.5 mr-1" />Aperçu PDF
            </Button>
            <Button size="sm" onClick={runNow} disabled={running}
              className="h-9 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
              data-testid="send-weekly-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Envoyer maintenant
            </Button>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-[#71717A] uppercase tracking-wider mb-2">Historique</p>
          {loading ? (
            <p className="text-xs text-[#71717A]">Chargement...</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-[#A1A1AA] italic">Aucun bilan envoyé pour l'instant.</p>
          ) : (
            <div className="space-y-1">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#E5E7EB] bg-white text-xs">
                  <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${h.email_sent ? 'text-[#10B981]' : 'text-[#A1A1AA]'}`} />
                  <span className="font-medium">Semaine {h.week_start}</span>
                  <span className="text-[#71717A]">
                    {h.totals?.total || 0} opps · {h.totals?.completion_rate || 0}% traités
                    {h.totals?.rdv ? ` · ${h.totals.rdv} RDV` : ''}
                    {h.totals?.overdue ? ` · ${h.totals.overdue} retards` : ''}
                  </span>
                  <span className="ml-auto text-[10px] text-[#A1A1AA]">
                    {h.sent_at ? new Date(h.sent_at).toLocaleString('fr-FR') : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
